import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useFavorites } from '../contexts/FavoritesContext';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import RemainingMacrosHeader from '../components/RemainingMacrosHeader';
import BrowseRemainingSummary from '../components/BrowseRemainingSummary';
import FilterSheet from '../components/FilterSheet';
import MealConflictPill from '../components/MealConflictPill';
import LeftoverRemoveDialog, { needsLeftoverRemoveConfirm } from '../components/LeftoverRemoveDialog';
import useUserStore from '../store/userStore';
import { mealPlanAPI, browseMealsAPI, profileAPI, guidedPlanningAPI } from '../utils/api';
import { loadBrowseMeals } from '../utils/browseMealsLoader';
import { toast } from 'sonner';
import { Search, ArrowLeft, Plus, Check, Heart, AlertTriangle, X, Clock, Filter, Calendar } from 'lucide-react';
import { getTodayString, formatDateString } from '../utils/dateUtils';
import { safeReturnPath, currentPath, buildUrl, finishFlow, continueFlowState } from '../utils/planLocation';
import { readSwapContext, swapContextParams, performSwap, swapErrorMessage } from '../utils/swapAction';
import { draftSwapRequest, rememberDraftSwapCandidates } from '../utils/draftSwapHandoff';
import { targetMealTypeForBrowse, mealTypeForBrowseSave } from '../utils/browseTargetSlot';
import { sortBrowseMeals } from '../utils/mealSort';
import { selectedFromDailyPlan, selectionForDate, addSelected, removeSelected, orderedSelection, addedMealIds, isLoggedEntry } from '../utils/browseSelection';
import { nutritionStateLabel, savedDayStatusLabel } from '../utils/planningStatus';
import { pendingSnackMealIds } from '../utils/guidedNextStep';
import {
  getSwapStatusLabel,
  shouldGreyCard,
  getSwapWarningBubble,
  getSwapConflictEntries,
  getCookTimeDisplay,
  formatMacroRow,
  getSwapHeaderText,
  SWAP_STATUS,
} from '../utils/swapStatus';
import {
  hasAllergenConflict,
  conflictText,
} from '../utils/mealConflicts';
import {
  buildBrowseFilterParams,
  countActiveFilters,
  splitFilterMatches,
  emptyBrowseFilters,
  browseFiltersFromProfile,
} from '../utils/browseFilterParams';

// Colour classes for the three Swap nutrition states.
function statusPillClass(label) {
  if (label === SWAP_STATUS.WITHIN) return 'bg-green-500/15 text-green-600';
  if (label === SWAP_STATUS.UNDER) return 'bg-blue-500/15 text-blue-600';
  if (label === SWAP_STATUS.OUT) return 'bg-orange-500/15 text-orange-600';
  return 'bg-secondary text-muted-foreground';
}

// Swap warning bubble: allergen red, a preference conflict amber, a same-day
// repeat neutral (information, not a warning; it never greys the card).
// Warning modal for incompatible meal selection (regular Browse only)
function WarningModal({ meal, warnings, onConfirm, onCancel, onAddAnotherDay, adding, contextDate }) {
  var [showDatePicker, setShowDatePicker] = useState(false);
  var [selectedDate, setSelectedDate] = useState('');

  var dateOptions = [];
  var today = new Date();
  for (var i = 0; i < 7; i++) {
    var date = new Date(today);
    date.setDate(today.getDate() + i);
    var dateStr = formatDateString(date);
    if (dateStr !== contextDate) {
      dateOptions.push({
        value: dateStr,
        label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      });
    }
  }

  function handleAddToDate() {
    if (selectedDate && onAddAnotherDay) onAddAnotherDay(selectedDate);
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-background rounded-2xl w-full max-w-sm p-6 shadow-xl"
        onClick={function(e) { e.stopPropagation(); }}
        data-testid="warning-modal"
      >
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
            <AlertTriangle size={24} className="text-amber-500" />
          </div>
        </div>
        <h3 className="font-heading font-bold text-lg text-center mb-2">Macro Warning</h3>
        <p className="text-sm text-center text-muted-foreground mb-4">
          Adding <span className="font-semibold text-foreground">{meal.name}</span> may affect your daily targets:
        </p>
        <div className="space-y-2 mb-6">
          {warnings.map(function(warning, idx) {
            return (
              <div key={idx} className="flex items-center gap-2 text-sm text-amber-600 bg-amber-500/10 px-3 py-2 rounded-lg">
                <AlertTriangle size={14} />
                <span>{warning}</span>
              </div>
            );
          })}
        </div>
        {showDatePicker ? (
          <div className="mb-4">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
              Choose a different day
            </label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {dateOptions.map(function(opt) {
                return (
                  <button
                    key={opt.value}
                    onClick={function() { setSelectedDate(opt.value); }}
                    className={'px-3 py-2 text-sm rounded-lg border transition-all ' +
                      (selectedDate === opt.value ? 'border-accent bg-accent/10 text-accent' : 'border-border hover:border-accent/50')}
                    data-testid={'date-option-' + opt.value}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Button onClick={function() { setShowDatePicker(false); setSelectedDate(''); }} variant="outline" size="sm" className="flex-1">Back</Button>
              <Button
                onClick={handleAddToDate}
                disabled={!selectedDate || adding}
                size="sm"
                className="flex-1 bg-accent"
                data-testid="confirm-add-to-date-btn"
              >
                {adding ? (<span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>)
                  : 'Add to ' + (dateOptions.find(function(o) { return o.value === selectedDate; })?.label || 'Date')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-3">
              <Button onClick={onCancel} variant="outline" className="flex-1 h-11 rounded-xl" data-testid="warning-cancel-btn">Cancel</Button>
              <Button onClick={onConfirm} disabled={adding} className="flex-1 h-11 rounded-xl bg-amber-500 text-white hover:bg-amber-600" data-testid="warning-confirm-btn">
                {adding ? (<span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>) : 'Add Anyway'}
              </Button>
            </div>
            <Button onClick={function() { setShowDatePicker(true); }} variant="ghost" className="w-full h-10 text-accent hover:text-accent hover:bg-accent/10" data-testid="add-another-day-btn">
              <Calendar size={16} className="mr-2" />
              Add to another day
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function selectedMacroLine(macros) {
  var r = function(v) { return Math.round(v || 0); };
  return r(macros.calories) + ' cal · ' + r(macros.protein) + 'g Protein · ' + r(macros.carbs) + 'g Carbs · ' + r(macros.fat) + 'g Fat';
}

// Status pill beside the meal type. Logged reuses TodaysLogSummary's blue
// Logged badge colours + check icon; Planned is neutral gray.
function SelectedStatusPill({ item }) {
  if (isLoggedEntry(item)) {
    return <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded-full bg-[#14D5F6]/10 text-[#14D5F6] text-[10px] leading-4" data-testid={'selected-status-' + item.planId}><Check size={10} />Logged</span>;
  }
  return <span className="inline-flex items-center px-1.5 py-px rounded-full bg-secondary text-muted-foreground text-[10px] leading-4" data-testid={'selected-status-' + item.planId}>Planned</span>;
}

// Every active saved meal of the day. Logged rows are locked here (no remove
// control); only Planned rows can be removed from this popup.
function SelectedMealsModal({ meals, remaining, status, removing, onRemove, onClose, onDone }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-2" onClick={onClose}>
      <div className="bg-background rounded-2xl w-full max-w-sm p-4 max-h-full flex flex-col shadow-xl" onClick={function(e) { e.stopPropagation(); }} data-testid="selected-meals-modal">
        <div className="flex items-center justify-between mb-3 shrink-0">
          <h3 className="font-heading font-bold text-lg">Selected meals</h3>
          <button onClick={onClose} aria-label="Close review"><X size={20} /></button>
        </div>
        {remaining && (
          <div className="mb-3 shrink-0">
            <BrowseRemainingSummary remaining={remaining} status={status} testId="selected-remaining-summary" caloriesLabel="Cals" />
          </div>
        )}
        <div className="space-y-2 overflow-y-auto min-h-0">
          {meals.map(function(item) {
            var logged = isLoggedEntry(item);
            return <div key={item.planId} className="rounded-xl bg-secondary/40 p-3" data-testid={'selected-row-' + item.planId}>
              <div className="flex items-center gap-3">
                {item.imageUrl && <img src={item.imageUrl} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />}
                <div className="min-w-0 flex-1"><p className="font-medium text-sm truncate">{item.name}</p><div className="flex items-center gap-1.5"><p className="text-xs text-muted-foreground">{item.mealType}</p><SelectedStatusPill item={item} /></div></div>
                {!logged && <Button variant="ghost" size="icon" className="shrink-0" disabled={removing === item.planId} onClick={function() { onRemove(item); }} data-testid={'remove-selected-' + item.planId} aria-label={'Remove ' + item.name}><X size={17} /></Button>}
              </div>
              <p className="text-[11px] font-normal text-muted-foreground mt-1.5 whitespace-nowrap" data-testid={'selected-macros-' + item.planId}>{item.macros ? selectedMacroLine(item.macros) : 'Updating nutrition…'}</p>
            </div>;
          })}
        </div>
        <div className="flex gap-2 mt-3 shrink-0">
          <Button className="flex-1" variant="outline" onClick={onClose} data-testid="selected-close-btn">Keep Browsing</Button>
          <Button className="flex-1 border-accent text-accent hover:bg-accent/10 hover:text-accent" variant="outline" onClick={onDone} data-testid="selected-done-btn">Finish &amp; Exit</Button>
        </div>
      </div>
    </div>
  );
}

function Meals() {
  var navigate = useNavigate();
  var location = useLocation();
  var [searchParams] = useSearchParams();
  var profile = useUserStore(function(state) { return state.profile; });

  var guidedMode = searchParams.get('guided') === 'true';
  var contextDate = searchParams.get('date') || getTodayString();
  var contextMealTypeRaw = searchParams.get('type') || '';
  var contextMealType = contextMealTypeRaw ? contextMealTypeRaw.charAt(0).toUpperCase() + contextMealTypeRaw.slice(1).toLowerCase() : '';
  // Where this browser returns when done: exactly where it was opened from
  // (only a same-app path is accepted).
  var returnTo = safeReturnPath(searchParams.get('return'), '/plan');
  var swapCtx = readSwapContext(searchParams);
  var isSwapMode = !!swapCtx;
  var replacePlanId = swapCtx ? swapCtx.replacePlanId : '';
  // Meal Review Swap: the same browser, judged against the unsaved Review
  // draft; a pick only goes back to that draft (see utils/draftSwapHandoff).
  var isDraftSwap = !!(swapCtx && swapCtx.isDraft);
  var showFavoritesOnly = searchParams.get('favorites') === 'true';
  // Opened by Step-by-Step's "Browse All <type> Options": an expanded picker
  // for that one step, so a successful pick returns to Step-by-Step, whose
  // saved-day status reload continues the flow exactly like a direct pick.
  var stepBrowse = guidedMode && !isSwapMode && searchParams.get('stepBrowse') === '1';
  // A browser opened for several days has no single date to add to — never
  // silently fall back to today (Browse & Choose is single-day only).
  var ambiguousDate = !searchParams.get('date') &&
    (searchParams.get('dates') || '').split(',').filter(Boolean).length > 1;

  var [meals, setMeals] = useState([]);
  var [browseData, setBrowseData] = useState(null);
  var [searchQuery, setSearchQuery] = useState('');
  // A Swap opens on the outgoing slot's tab; the user is free to change it.
  var [activeCategory, setActiveCategory] = useState(showFavoritesOnly ? 'Favorites' : (contextMealType || 'All'));
  var [loading, setLoading] = useState(true);
  var [adding, setAdding] = useState(null);
  var [remaining, setRemaining] = useState(null);
  var [target, setTarget] = useState(null);
  var { favoriteIds, toggleFavorite: ctxToggleFavorite } = useFavorites();
  var favorites = useMemo(function() {
    var m = {};
    favoriteIds.forEach(function(id) { m[id] = true; });
    return m;
  }, [favoriteIds]);

  // Selected: { plan_id: entry } (utils/browseSelection) -- every active saved
  // meal of the day (planned + logged) from /meal-plans/daily, the source of
  // truth; a new add shows at once and is then replaced by the saved row.
  var [savedSelections, setSavedSelections] = useState({});
  var addedMeals = useMemo(function() { return selectionForDate(savedSelections, contextDate); }, [savedSelections, contextDate]);
  var activeDateRef = useRef(contextDate);
  activeDateRef.current = contextDate;
  var selectedRequestVersionRef = useRef(0);
  // Card "Added" state stays limited to this visit's adds (saved rows of the
  // same recipe never block adding it to another slot).
  var [visitAddedPlanIds, setVisitAddedPlanIds] = useState({});
  var addedIds = useMemo(function() {
    var visit = {};
    Object.keys(addedMeals).forEach(function(pid) { if (visitAddedPlanIds[pid]) visit[pid] = addedMeals[pid]; });
    return addedMealIds(visit);
  }, [addedMeals, visitAddedPlanIds]);
  var [showSelected, setShowSelected] = useState(false);
  var [removingPlanId, setRemovingPlanId] = useState(null);
  var [leftoverRemoveConfirm, setLeftoverRemoveConfirm] = useState(null); // {planId, mealName, role, entry} | null

  // Multi-select filter state.
  var [showFilters, setShowFilters] = useState(false);
  // Use persisted profile data synchronously as a temporary default, but gate
  // meal loading until the canonical saved cooking preferences API resolves.
  var [selectedFilters, setSelectedFilters] = useState(function() {
    return browseFiltersFromProfile(profile);
  });
  var [preferencesReady, setPreferencesReady] = useState(false);
  var filtersTouchedRef = useRef(false);
  var requestSequenceRef = useRef(0);

  // Available filter options (loaded from API)
  var [filterOptions, setFilterOptions] = useState({ tags: [], cuisines: [], proteins: [], cookware: [], dishTypes: [] });

  var [warningMeal, setWarningMeal] = useState(null);
  var [loadError, setLoadError] = useState(false);

  var categories = ['All', 'Breakfast', 'Lunch', 'Dinner', 'Snack', 'Favorites'];

  var activeFilterCount = countActiveFilters(selectedFilters);
  var addedCount = Object.keys(addedMeals).length;

  var targetsReady = !!(browseData && browseData.targets_ready);
  var ranges = browseData ? browseData.ranges : null;

  // The backend cooking-preferences endpoint is authoritative: Settings saves
  // there without necessarily refreshing persisted Zustand profile state.
  // Never overwrite a local session edit if this response arrives late.
  useEffect(function() {
    var cancelled = false;
    profileAPI.getCookingPreferences()
      .then(function(res) {
        if (!cancelled && !filtersTouchedRef.current) {
          setSelectedFilters(browseFiltersFromProfile((res && res.data) || null));
        }
      })
      .catch(function(err) {
        console.error('Failed to load saved cooking preferences:', err);
        // Keep the synchronous persisted-profile fallback on a network error.
      })
      .then(function() {
        if (!cancelled) setPreferencesReady(true);
      });
    return function() { cancelled = true; };
  }, []);

  // Load filter options once on mount
  useEffect(function() {
    browseMealsAPI.getFilterOptions()
      .then(function(res) {
        var data = res.data;
        setFilterOptions({
          tags: data.tags || [],
          cuisines: data.cuisines || [],
          proteins: data.protein_types || [],
          cookware: data.cookware || [],
          dishTypes: data.dish_types || [],
        });
      })
      .catch(function(err) {
        console.error('Failed to load filter options:', err);
      });
  }, []);

  // Fetch meals with macro compatibility info
  var fetchData = useCallback(function() {
    if (!preferencesReady) return Promise.resolve();
    var requestId = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestId;
    setLoading(true);
    var categoryToFetch = activeCategory === 'Favorites' || activeCategory === 'All' ? null : activeCategory;
    var filters = buildBrowseFilterParams(selectedFilters);

    loadBrowseMeals(
      function() {
        if (isDraftSwap) {
          return browseMealsAPI.getMacroAware(
            contextDate,
            categoryToFetch,
            filters,
            null,
            targetMealTypeForBrowse(contextMealType, isSwapMode),
            draftSwapRequest()
          );
        }
        return browseMealsAPI.getMacroAware(
          contextDate,
          categoryToFetch,
          filters,
          isSwapMode ? replacePlanId : null,
          targetMealTypeForBrowse(contextMealType, isSwapMode)
        );
      },
      {
        onSuccess: function(data) {
          if (requestId !== requestSequenceRef.current) return;
          if (isDraftSwap) rememberDraftSwapCandidates(data.meals);
          setMeals(data.meals);
          setBrowseData(data);
          setRemaining(data.remaining);
          setTarget(data.target);
          setLoadError(false);
        },
        onError: function(error) {
          if (requestId !== requestSequenceRef.current) return;
          console.error('Failed to fetch macro-aware meals:', error);
          setMeals([]);
          setBrowseData(null);
          setLoadError(true);
          toast.error('Failed to load meals');
        },
      }
    ).finally(function() {
      if (requestId === requestSequenceRef.current) setLoading(false);
    });
  }, [preferencesReady, contextDate, contextMealType, activeCategory, selectedFilters, isSwapMode, replacePlanId, isDraftSwap]);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  // Saved-day status from the same endpoint/rules as the Step-by-Step Day Overview.
  var [dayStatus, setDayStatus] = useState(null);
  var statusSequenceRef = useRef(0);
  var refreshDayStatus = useCallback(function() {
    if (isSwapMode) return;
    var requestId = statusSequenceRef.current + 1;
    statusSequenceRef.current = requestId;
    Promise.resolve()
      .then(function() { return guidedPlanningAPI.getStatus(contextDate); })
      .then(function(res) { if (requestId === statusSequenceRef.current) setDayStatus(savedDayStatusLabel(res && res.data)); })
      .catch(function() { if (requestId === statusSequenceRef.current) setDayStatus(null); });
  }, [contextDate, isSwapMode]);

  useEffect(function() {
    refreshDayStatus();
  }, [refreshDayStatus]);

  // Reload the day's saved rows. Only the newest request for the active date
  // may write, so a late response never overwrites a newer selection or
  // loads another date's meals into this popup.
  function reloadSavedDay(date) {
    var version = ++selectedRequestVersionRef.current;
    return mealPlanAPI.getDailyPlan(date).then(function(res) {
      if (activeDateRef.current !== date || version !== selectedRequestVersionRef.current) return;
      setSavedSelections(selectedFromDailyPlan(res && res.data, date));
    });
  }

  // Seed Selected with the day's saved meals (also after returning from a
  // meal's detail screen). Each date starts with its own selection; loading,
  // failure, Back and Finish never delete saved records.
  useEffect(function() {
    setSavedSelections({});
    setVisitAddedPlanIds({});
    setShowSelected(false);
    setLeftoverRemoveConfirm(null);
    setRemovingPlanId(null);
    if (isSwapMode || ambiguousDate) {
      selectedRequestVersionRef.current++;
      return;
    }
    reloadSavedDay(contextDate).catch(function(err) {
      console.error("Failed to load the day's saved meals:", err);
    });
  }, [contextDate, isSwapMode, ambiguousDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // A just-saved meal: listed at once, then reconciled with its saved row.
  function selectNewlySaved(entry) {
    setSavedSelections(function(current) { return addSelected(selectionForDate(current, entry.date), entry); });
    setVisitAddedPlanIds(function(current) { return Object.assign({}, current, { [entry.planId]: true }); });
    reloadSavedDay(entry.date).catch(function(err) {
      console.error('Failed to refresh saved meals after adding:', err);
    });
  }

  // Category + search filtering, then ordering (clone; never mutate state).
  // A Phase 2B response is kept in the backend's server_rank order exactly;
  // the legacy client ordering only applies to a response without it.
  var orderedMeals = useMemo(function() {
    var base = (meals || []).filter(function(meal) {
      if (activeCategory === 'Favorites') return favorites[meal.meal_id];
      if (activeCategory !== 'All') return meal.category === activeCategory;
      return true;
    });
    if (searchQuery) {
      var query = searchQuery.toLowerCase();
      base = base.filter(function(meal) { return (meal.name || '').toLowerCase().includes(query); });
    }
    return sortBrowseMeals(base, { isSwapMode: isSwapMode, activeCategory: activeCategory, favorites: favorites });
  }, [meals, searchQuery, activeCategory, favorites, isSwapMode]);

  var sections = useMemo(function() { return splitFilterMatches(orderedMeals); }, [orderedMeals]);

  // Quick add meal (or swap if in swap mode)
  function quickAddMeal(meal, e) {
    if (e) e.stopPropagation();
    // Greying is guidance only: every card adds in one press. The card's
    // backend nutrition_state is the only nutrition judgement, so the legacy
    // fixed-buffer fits / warnings never ask for an "Add anyway" confirmation.
    executeAddMeal(meal);
  }

  function executeAddMeal(meal) {
    if (!isSwapMode && ambiguousDate) {
      toast.error('Choose a single day to add meals to.');
      return;
    }
    setAdding(meal.meal_id);
    var mealType = mealTypeForBrowseSave(contextMealType, meal.category);

    if (isSwapMode) {
      // The shared Swap write (also used by a candidate's detail screen).
      performSwap(swapCtx, { date: contextDate, mealType: mealType, meal: meal })
        .then(function() {
          toast.success('Swapped to ' + meal.name);
          setWarningMeal(null);
          // Pop straight back to where the Swap started, so the completed Swap
          // (its outgoing row no longer exists) never stays behind Back.
          finishFlow(navigate, location, returnTo);
        })
        .catch(function(error) {
          console.error('Failed to swap meal:', error);
          toast.error(swapErrorMessage(error, swapCtx));
        })
        .finally(function() { setAdding(null); });
      return;
    }

    // Step-by-Step's Snack step: the same duplicate-Snack protection as its
    // direct pick, checked against the saved day before saving.
    var isDuplicateSnack = stepBrowse && mealType === 'Snack'
      ? mealPlanAPI.getDailyPlan(contextDate).then(function(res) { return !!pendingSnackMealIds(res && res.data)[meal.meal_id]; })
      : Promise.resolve(false);
    isDuplicateSnack
      .then(function(duplicate) {
        if (duplicate) {
          toast.error(meal.name + ' is already added as a Snack');
          return null;
        }
        return mealPlanAPI.addMealPlan({
          date: contextDate,
          meal_type: mealType,
          meal_id: meal.meal_id,
          meal_name: meal.name,
          source: guidedMode ? 'guided' : 'browse',
        });
      })
      .then(function(response) {
        if (!response) return;
        toast.success(meal.name + ' added to plan');
        if (activeDateRef.current !== contextDate) return;
        if (stepBrowse) {
          goBack();
          return;
        }
        setWarningMeal(null);
        selectNewlySaved({
          planId: response.data.plan_id, mealId: meal.meal_id, name: meal.name,
          mealType: mealType, date: contextDate, status: 'pending', imageUrl: meal.image_url, macros: null,
        });
        fetchData();
        refreshDayStatus();
      })
      .catch(function(error) {
        console.error('Failed to add meal:', error);
        toast.error('Failed to add meal');
      })
      .finally(function() { setAdding(null); });
  }

  function confirmWarningAdd() { if (warningMeal) executeAddMeal(warningMeal); }

  function addMealToAnotherDay(targetDate) {
    if (!warningMeal) return;
    setAdding(warningMeal.meal_id);
    var mealType = mealTypeForBrowseSave(contextMealType, warningMeal.category);
    mealPlanAPI.addMealPlan({
      date: targetDate,
      meal_type: mealType,
      meal_id: warningMeal.meal_id,
      meal_name: warningMeal.name,
      source: 'browse',
    })
      .then(function(response) {
        var dateLabel = new Date(targetDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        toast.success(warningMeal.name + ' added to ' + dateLabel);
        if (activeDateRef.current !== contextDate) return;
        setWarningMeal(null);
        // The write is immediate, but only the active day's rows belong in Selected.
        if (targetDate === contextDate) {
          selectNewlySaved({
            planId: response.data.plan_id, mealId: warningMeal.meal_id, name: warningMeal.name,
            mealType: mealType, date: targetDate, status: 'pending', imageUrl: warningMeal.image_url, macros: null,
          });
          fetchData();
          refreshDayStatus();
        }
      })
      .catch(function(error) {
        console.error('Failed to add meal:', error);
        toast.error('Failed to add meal');
      })
      .finally(function() { setAdding(null); });
  }

  function cancelWarning() { setWarningMeal(null); }

  // Only Planned rows are removable here: a Logged row is never sent to
  // delete from this popup. Linked leftovers use the same confirmation as Plan.
  function removeSelectedMeal(item) {
    if (!item || isLoggedEntry(item)) return;
    if (needsLeftoverRemoveConfirm(item.leftoverRole)) {
      setLeftoverRemoveConfirm({ planId: item.planId, mealName: item.name, role: item.leftoverRole, entry: item });
      return;
    }
    doRemoveSelectedMeal(item);
  }

  function doRemoveSelectedMeal(item) {
    if (!item || isLoggedEntry(item)) return;
    setRemovingPlanId(item.planId);
    mealPlanAPI.deleteMealPlan(item.planId, getTodayString())
      .then(function() {
        toast.success(item.name + ' removed from plan');
        if (activeDateRef.current !== item.date) return;
        setSavedSelections(function(current) { return removeSelected(selectionForDate(current, item.date), item); });
        fetchData();
        refreshDayStatus();
        // The backend can keep or cascade a linked partner: reconcile from the
        // saved rows instead of assuming only the clicked row disappeared.
        return reloadSavedDay(item.date).catch(function(error) {
          console.error('Failed to refresh saved meals after removal:', error);
          toast.error('Meal removed. Refresh to check the remaining linked meals.');
        });
      })
      .catch(function(error) {
        console.error('Failed to remove selected meal:', error);
        toast.error('Failed to remove meal');
      })
      .finally(function() { setRemovingPlanId(null); });
  }

  function toggleFavorite(mealId, e) {
    if (e) e.stopPropagation();
    ctxToggleFavorite(mealId);
  }

  function viewMeal(mealId) {
    var params = { return: currentPath(location) };
    if (guidedMode) params.guided = 'true';
    if (contextDate) params.date = contextDate;
    if (contextMealType) params.type = contextMealType;
    if (isSwapMode) {
      // A candidate's detail stays part of the Swap: it swaps (never adds),
      // and finishes the whole Swap back at the Swap's own origin.
      Object.assign(params, swapContextParams(swapCtx), { flowReturn: returnTo });
    } else if (guidedMode) {
      // Ordinary Guided Browse results are unsaved Guided candidates. Swap
      // details remain governed exclusively by their explicit Swap context.
      params.candidate = '1';
      if (stepBrowse) params.stepBrowse = '1';
    }
    navigate(buildUrl('/meal-view/' + mealId, params), { state: continueFlowState(location) });
  }

  // Back / Done: return to exactly where this browser was opened from.
  function goBack() {
    finishFlow(navigate, location, returnTo);
  }

  function handleSearchChange(e) { setSearchQuery(e.target.value); }

  function clearAllFilters() {
    filtersTouchedRef.current = true;
    setSelectedFilters(emptyBrowseFilters());
  }

  function applyFilters(filters) {
    filtersTouchedRef.current = true;
    setSelectedFilters(filters);
  }

  var favCount = Object.keys(favorites).length;

  // Header subtitle
  var subtitle = null;
  if (isSwapMode) {
    subtitle = getSwapHeaderText(browseData); // null when targets not ready
    if (!subtitle && !loading && meals.length > 0) subtitle = 'Choose a replacement';
  } else if (remaining) {
    subtitle = 'Choose a meal';
  }

  // Category tabs
  var categoryTabs = categories.map(function(cat) {
    var isActive = activeCategory === cat;
    var isFavTab = cat === 'Favorites';
    return (
      <button
        key={cat}
        onClick={function() { setActiveCategory(cat); }}
        className={'px-3 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 shrink-0 ' +
          (isActive ? 'bg-accent text-white' : 'text-muted-foreground hover:bg-secondary')}
        data-testid={'category-' + cat.toLowerCase()}
      >
        {isFavTab && (<Heart size={14} fill={isActive ? 'currentColor' : 'none'} />)}
        {isFavTab ? (favCount > 0 ? favCount : 'Favorites') : cat}
      </button>
    );
  });

  // ---- Single meal card renderer (shared by Best / Other sections) ----
  function renderMealCard(meal, index) {
    var isAdding = adding === meal.meal_id;
    var isFavorite = favorites[meal.meal_id];
    var isAdded = addedIds[meal.meal_id] || false;

    // A matched allergen OR allergen safety the backend could not verify
    // (Phase 2B reports both as the `allergen` conflict).
    var hasAllergen = hasAllergenConflict(meal);
    var hasDislike = meal.has_dislike || false;
    var missingCookware = (meal.missing_cookware || []).filter(function(id) { return id !== 'no_cook'; });
    var exceedsCookTime = meal.exceeds_cook_time || false;

    // Server-owned projected-day status plus the shared manual-choice warning.
    var statusLabel = isSwapMode
      ? getSwapStatusLabel(meal, { targetsReady: targetsReady, ranges: ranges })
      : (targetsReady ? nutritionStateLabel(meal.nutrition_state) : null);
    var swapEntries = getSwapConflictEntries(meal);
    var warningBubble = getSwapWarningBubble(meal);
    var cookTimeText = getCookTimeDisplay(meal);

    // Manual-choice conflicts the backend reported (Phase 2B: allergen,
    // dislike, Don't Suggest, cookware, cook time, a same-day family / group
    // repeat): labelled, never disabling. Both Browse and Swap show the same
    // primary image pill; any remaining conflicts share one muted line.
    var noteEntries = swapEntries.slice(1);
    var conflictNote = noteEntries.map(conflictText).join(' · ');

    var isGreyedOut;
    if (isSwapMode) {
      isGreyedOut = !isAdded && shouldGreyCard(meal, statusLabel);
    } else {
      // Legacy fits / warnings never grey: only a manual-choice conflict or a
      // canonical over-condition (over_macros) does, as in Swap.
      isGreyedOut = !isAdded && shouldGreyCard(meal, null);
    }

    var borderClass = 'border-border/40 hover:border-accent/50';
    if (isAdded) borderClass = 'border-green-500/50 bg-green-500/5';
    else if (hasAllergen) borderClass = 'border-red-500/50 bg-red-500/5';
    else if (hasDislike) borderClass = 'border-orange-500/50 bg-orange-500/5';
    else if (missingCookware.length > 0 || exceedsCookTime) borderClass = 'border-yellow-500/30 bg-yellow-500/5';
    else if (isGreyedOut) borderClass = 'border-amber-500/30 bg-amber-500/5';

    return (
      <motion.div
        key={meal.meal_id}
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: isGreyedOut ? 0.7 : 1, y: 0 }}
        transition={{ delay: Math.min(index, 12) * 0.02 }}
        className={'bg-card border rounded-xl overflow-hidden transition-all ' + borderClass}
        data-testid={'meal-card-' + meal.meal_id}
      >
        {/* Image + Quick Actions */}
        <div className="relative h-28 bg-secondary overflow-hidden">
          {meal.image_url && (
            <img src={meal.image_url} alt={meal.name} className="w-full h-full object-cover" onClick={function() { viewMeal(meal.meal_id); }} />
          )}

          {/* Favorite button */}
          <button
            onClick={function(e) { toggleFavorite(meal.meal_id, e); }}
            className={'absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ' +
              (isFavorite ? 'bg-red-500 text-white' : 'bg-black/40 text-white hover:bg-black/60')}
            data-testid={'favorite-' + meal.meal_id}
          >
            <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
          </button>

          {/* Quick add / swap button — always visibly active (greying never disables) */}
          <button
            onClick={function(e) { if (!isAdded) quickAddMeal(meal, e); }}
            disabled={isAdding || isAdded}
            className={'absolute top-2 right-2 w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-md ' +
              (isAdded ? 'bg-green-500 text-white cursor-default' : 'bg-accent text-white hover:bg-accent/90')}
            data-testid={'quick-add-' + meal.meal_id}
            title={isAdded ? 'Added to plan' : (isSwapMode ? 'Swap to this meal' : 'Add to plan')}
          >
            {isAdding ? (<span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>)
              : isAdded ? (<Check size={20} />) : (<Plus size={20} />)}
          </button>

          {/* ONE shared, compact warning pill for both Browse and Swap. */}
          {warningBubble && <MealConflictPill meal={meal} testId={'swap-warning-' + meal.meal_id} />}
        </div>

        {/* Info */}
        <div className="p-3 cursor-pointer" onClick={function() { viewMeal(meal.meal_id); }}>
          <h3 className="font-heading font-semibold text-sm mb-1 truncate">{meal.name}</h3>

          {/* Compact, icon-free macro row: "590 cal · 37P · 74C · 18F" */}
          <p className="text-xs text-muted-foreground mb-1" data-testid={'macro-row-' + meal.meal_id}>
            {formatMacroRow(meal)}
          </p>

          {isSwapMode ? (
            <>
              {/* Nutrition status (left) + cook time (right) */}
              <div className="flex items-center justify-between gap-2 mb-1 min-h-[20px]">
                {statusLabel ? (
                  <span className={'text-[11px] px-2 py-0.5 rounded-full font-medium ' + statusPillClass(statusLabel)} data-testid={'swap-status-' + meal.meal_id}>
                    {statusLabel}
                  </span>
                ) : <span />}
                {cookTimeText && (
                  <span className="text-xs text-muted-foreground flex items-center gap-0.5 shrink-0" data-testid={'cook-time-' + meal.meal_id}>
                    {cookTimeText !== 'No cook' && <Clock size={11} />}{cookTimeText}
                  </span>
                )}
              </div>

              {/* One row of recipe tags (never wraps) */}
              {meal.tags && meal.tags.length > 0 && (
                <div className="flex gap-1 overflow-hidden whitespace-nowrap" data-testid={'tags-row-' + meal.meal_id}>
                  {meal.tags.slice(0, 2).map(function(tag) {
                    return (
                      <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-accent/10 text-accent rounded-full shrink-0">{tag}</span>
                    );
                  })}
                  {meal.tags.length > 2 && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-secondary text-muted-foreground rounded-full shrink-0">+{meal.tags.length - 2}</span>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Regular Browse: canonical nutrition status + cook time. */}
              <div className="flex items-center justify-between gap-2 mb-1">
                {statusLabel ? (
                  <span className={'text-[11px] px-2 py-0.5 rounded-full font-medium ' + statusPillClass(statusLabel)} data-testid={'browse-status-' + meal.meal_id}>
                    {statusLabel}
                  </span>
                ) : <span />}
                {cookTimeText && (
                  <span className="text-xs text-muted-foreground flex items-center gap-0.5 shrink-0">
                    {cookTimeText !== 'No cook' && <Clock size={11} />}{cookTimeText}
                  </span>
                )}
              </div>

              {/* One row of recipe tags (never wraps) */}
              {meal.tags && meal.tags.length > 0 && (
                <div className="flex gap-1 overflow-hidden whitespace-nowrap" data-testid={'tags-row-' + meal.meal_id}>
                  {meal.tags.slice(0, 2).map(function(tag) {
                    return (
                      <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-accent/10 text-accent rounded-full shrink-0">{tag}</span>
                    );
                  })}
                  {meal.tags.length > 2 && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-secondary text-muted-foreground rounded-full shrink-0">+{meal.tags.length - 2}</span>
                  )}
                </div>
              )}
            </>
          )}

          {/* Further manual-choice conflicts (information only, never greying) */}
          {conflictNote && (
            <p className="text-[11px] text-muted-foreground truncate mt-1" data-testid={'conflict-note-' + meal.meal_id} title={conflictNote}>
              {conflictNote}
            </p>
          )}
        </div>
      </motion.div>
    );
  }

  function renderGrid(list) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {list.map(function(meal, index) { return renderMealCard(meal, index); })}
      </div>
    );
  }

  var hasResults = sections.best.length > 0 || sections.other.length > 0;

  return (
    <div className="min-h-screen pb-bottom-nav bg-background">
      {/* Header (sticky) */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        <div className="px-4 pb-4">
          {isSwapMode ? (
            <div className="flex items-center gap-3 mb-3">
              <Button onClick={goBack} data-testid="back-btn" variant="ghost" size="icon" className="rounded-full h-9 w-9">
                <ArrowLeft size={18} />
              </Button>
              <div className="flex-1">
                <h1 className="font-heading font-bold text-lg">
                  {contextMealType ? 'Swap ' + contextMealType : 'Browse Meals'}
                </h1>
                {subtitle && (<p className="text-xs text-muted-foreground" data-testid="swap-header-count">{subtitle}</p>)}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-3" data-testid="browse-header-row">
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-lg whitespace-nowrap">
                  {guidedMode && contextMealType ? 'Choose ' + contextMealType : 'Browse Meals'}
                </h1>
                {subtitle && (<p className="text-xs text-muted-foreground" data-testid="swap-header-count">{subtitle}</p>)}
              </div>
              <div className="ml-auto pl-1 flex items-center gap-1 shrink-0 -mr-2">
                {addedCount > 0 && (
                  <button onClick={function() { setShowSelected(true); }} className="text-xs font-medium text-accent whitespace-nowrap" data-testid="review-selected-btn">View Meals ({addedCount})</button>
                )}
                <button onClick={goBack} className="h-9 w-9 flex items-center justify-center shrink-0 rounded-full" aria-label="Done" data-testid="done-adding-btn">
                  <span className="h-7 w-7 rounded-full border border-input bg-secondary shadow-sm text-foreground flex items-center justify-center"><Check size={15} /></span>
                </button>
              </div>
            </div>
          )}

          {/* Remaining macros header */}
          {remaining && (isSwapMode ? (
            <div className="mb-3 p-3 bg-secondary/30 rounded-xl">
              <RemainingMacrosHeader
                remaining={remaining}
                target={target}
                compact={true}
                label="Remaining to target"
              />
            </div>
          ) : (
            <div className="mb-3">
              <BrowseRemainingSummary remaining={remaining} status={dayStatus} testId="browse-remaining-summary" />
            </div>
          ))}

          {/* Search + Filter button */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Search meals..."
                value={searchQuery}
                onChange={handleSearchChange}
                data-testid="search-meals-input"
                className="pl-10 h-10 bg-secondary/30 border-transparent focus:border-accent"
              />
            </div>
            <Button
              variant={activeFilterCount > 0 ? 'default' : 'outline'}
              size="icon"
              onClick={function() { setShowFilters(true); }}
              className={'h-10 w-10 rounded-xl relative ' + (activeFilterCount > 0 ? 'bg-accent text-white' : '')}
              data-testid="filter-toggle-btn"
            >
              <Filter size={18} />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-xs rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>

          {/* Category tabs (single scrollable row) */}
          <div className="flex gap-1 overflow-x-auto pb-1">
            {categoryTabs}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : loadError ? (
        <div className="p-4">
          <div className="text-center py-12" data-testid="meals-load-error">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
              <AlertTriangle size={24} className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-2">Couldn&apos;t load meals</p>
            <p className="text-xs text-muted-foreground mb-4">Please check your connection and try again</p>
            <Button onClick={fetchData} size="sm" className="rounded-full" data-testid="meals-retry-btn">Retry</Button>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-5">
          {hasResults ? (
            sections.sectioned ? (
              <>
                {sections.best.length > 0 && (
                  <div data-testid="best-matches-section">
                    <h2 className="text-sm font-semibold text-foreground mb-2">Best matches</h2>
                    {renderGrid(sections.best)}
                  </div>
                )}
                {sections.other.length > 0 && (
                  <div data-testid="other-meals-section">
                    <h2 className="text-sm font-semibold text-muted-foreground mb-2">Other meals</h2>
                    {renderGrid(sections.other)}
                  </div>
                )}
              </>
            ) : (
              renderGrid(sections.best)
            )
          ) : (
            <div className="text-center py-12" data-testid="no-meals-found">
              {activeCategory === 'Favorites' ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
                    <Heart size={24} className="text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground mb-2">No favorites yet</p>
                  <p className="text-xs text-muted-foreground mb-4">Tap the heart on any meal to save it</p>
                  <Button onClick={function() { setActiveCategory('All'); }} size="sm" className="rounded-full" data-testid="show-all-meals-btn">Browse All Meals</Button>
                </>
              ) : activeFilterCount > 0 ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
                    <Filter size={24} className="text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground mb-2">No meals match your filters</p>
                  <p className="text-xs text-muted-foreground mb-4">Try adjusting your filter criteria</p>
                  <Button onClick={clearAllFilters} size="sm" variant="outline" className="rounded-full" data-testid="clear-all-filters-btn">Clear All Filters</Button>
                </>
              ) : (
                <p className="text-muted-foreground">No meals found</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filter bottom sheet */}
      <FilterSheet
        open={showFilters}
        onOpenChange={setShowFilters}
        options={filterOptions}
        value={selectedFilters}
        onApply={applyFilters}
      />

      {/* Warning Modal (regular Browse only) */}
      {warningMeal && (
        <WarningModal
          meal={warningMeal}
          warnings={warningMeal.warnings}
          onConfirm={confirmWarningAdd}
          onCancel={cancelWarning}
          onAddAnotherDay={addMealToAnotherDay}
          adding={adding === warningMeal.meal_id}
          contextDate={contextDate}
        />
      )}

      {/* Hidden (not closed) while a linked-leftover removal is confirmed: the
          shared AlertDialog layer sits below this popup's z-[60]. */}
      {showSelected && !leftoverRemoveConfirm && <SelectedMealsModal meals={orderedSelection(addedMeals)} remaining={remaining} status={dayStatus} removing={removingPlanId} onRemove={removeSelectedMeal} onClose={function() { setShowSelected(false); }} onDone={function() { setShowSelected(false); goBack(); }} />}

      <LeftoverRemoveDialog
        pending={leftoverRemoveConfirm}
        onCancel={function() { setLeftoverRemoveConfirm(null); }}
        onConfirm={function(p) { doRemoveSelectedMeal(p.entry); }}
      />

      {!guidedMode && <BottomNav />}
    </div>
  );
}

export default Meals;
